import type { EventListItem, MarketListItem } from '@/types/market'
import type {
	BackendEventsParams,
	BackendEventsResponse,
	BackendMarketsResponse,
} from './backend-types'
import { normalizeBackendEvents, normalizeBackendMarkets } from './normalizers/backend'

export type BackendEventsPaginated = {
	data: EventListItem[]
	meta: {
		total: number
		limit: number
		offset: number
		hasMore: boolean
	}
}

export type BackendMarketsPaginated = {
	data: MarketListItem[]
	meta: {
		total: number
		limit: number
		offset: number
		hasMore: boolean
	}
}

function applyCommonParams(url: URL, params: BackendEventsParams) {
	if (params.q) url.searchParams.set('q', params.q)
	if (params.category) url.searchParams.set('category', params.category)
	if (params.source) url.searchParams.set('source', params.source)
	if (params.status) url.searchParams.set('status', params.status)
	if (params.sort) url.searchParams.set('sort', params.sort)
	if (params.order) url.searchParams.set('order', params.order)
	if (params.limit) url.searchParams.set('limit', String(params.limit))
	if (params.offset) url.searchParams.set('offset', String(params.offset))
	if (params.expiring_soon) url.searchParams.set('expiring_soon', 'true')
	if (params.closing_time) url.searchParams.set('closing_time', params.closing_time)
	if (params.min_probability !== undefined) {
		url.searchParams.set('min_probability', String(params.min_probability))
	}
	if (params.min_volume !== undefined) {
		url.searchParams.set('min_volume', String(params.min_volume))
	}
	if (params.max_volume !== undefined) {
		url.searchParams.set('max_volume', String(params.max_volume))
	}
	if (params.min_liquidity !== undefined) {
		url.searchParams.set('min_liquidity', String(params.min_liquidity))
	}
	if (params.max_liquidity !== undefined) {
		url.searchParams.set('max_liquidity', String(params.max_liquidity))
	}
}

async function fetchFromBackend<TResponse extends { data: any[]; meta: any }, TNormalized>(
	endpoint: string,
	params: BackendEventsParams,
	normalizer: (data: any[]) => TNormalized[],
	errorMessage: string
): Promise<{ data: TNormalized[]; meta: TResponse['meta'] & { hasMore: boolean } }> {
	const url = new URL(endpoint, window.location.origin)
	applyCommonParams(url, params)

	const response = await fetch(url.toString())

	if (!response.ok) {
		throw new Error(`${errorMessage}: ${response.status}`)
	}

	const json: TResponse = await response.json()

	return {
		data: normalizer(json.data),
		meta: {
			...json.meta,
			hasMore: json.meta.offset + json.data.length < json.meta.total,
		},
	}
}

export async function fetchBackendEvents(
	params: BackendEventsParams
): Promise<BackendEventsPaginated> {
	return fetchFromBackend<BackendEventsResponse, EventListItem>(
		'/api/events',
		params,
		normalizeBackendEvents,
		'Failed to fetch events'
	)
}

export async function fetchBackendMarkets(
	params: BackendEventsParams
): Promise<BackendMarketsPaginated> {
	return fetchFromBackend<BackendMarketsResponse, MarketListItem>(
		'/api/markets',
		params,
		normalizeBackendMarkets,
		'Failed to fetch markets'
	)
}
